"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

type Axis = "x" | "y";
type Sign = 1 | -1;
type Phase = "idle" | "cover" | "reveal";

type Travel = { axis: Axis; sign: Sign };

const EASE = "cubic-bezier(0.45, 0, 0.55, 1)";
const COVER_MS = 380;
const REVEAL_MS = 320;

const STACK = ["/news", "/about", "/sustainability", "/people", "/careers", "/contact"] as const;
const NEWS_TABS = ["/news", "/news/events", "/news/awards", "/news/lectures"] as const;

/**
 * White directional page slide (big.dk §H). Overlay travels with the route:
 * projects ↔ rest is horizontal; news tabs and studio pages are vertical.
 * Skipped for project open/close and when the user prefers reduced motion.
 */
export function PageTransition() {
  const pathname = usePathname();
  const prev = useRef(pathname);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [travel, setTravel] = useState<Travel>({ axis: "x", sign: 1 });
  const [gen, setGen] = useState(0);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (prev.current === pathname) return;
    const from = prev.current;
    const to = pathname;
    prev.current = pathname;

    if (reduced) return;
    if (shouldSkip(from, to)) return;

    setTravel(travelFor(from, to));
    setPhase("cover");
    setGen((g) => g + 1);
  }, [pathname, reduced]);

  useLayoutEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    const toTranslate = (n: number) =>
      travel.axis === "x" ? `translate3d(${n}%,0,0)` : `translate3d(0,${n}%,0)`;

    switch (phase) {
      case "idle":
        return;
      case "cover": {
        el.style.transition = "none";
        el.style.transform = toTranslate(-travel.sign * 100);
        void el.offsetHeight;
        el.style.transition = `transform ${COVER_MS / 1000}s ${EASE}`;
        el.style.transform = "translate3d(0,0,0)";
        const t = window.setTimeout(() => setPhase("reveal"), COVER_MS);
        return () => window.clearTimeout(t);
      }
      case "reveal": {
        el.style.transition = `transform ${REVEAL_MS / 1000}s ${EASE}`;
        el.style.transform = toTranslate(travel.sign * 100);
        const t = window.setTimeout(() => setPhase("idle"), REVEAL_MS);
        return () => window.clearTimeout(t);
      }
      default: {
        const _never: never = phase;
        return _never;
      }
    }
  }, [phase, travel, gen]);

  return (
    <div
      ref={overlayRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1000] bg-white"
      style={{ visibility: phase === "idle" ? "hidden" : "visible" }}
    />
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

function shouldSkip(from: string, to: string) {
  if (isProjectSlug(to) || isProjectSlug(from)) return true;
  return isProjectPath(from) && isProjectPath(to);
}

function isProjectPath(path: string) {
  return path === "/" || path === "/projects" || path.startsWith("/projects");
}

function isProjectSlug(path: string) {
  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "projects" || parts.length !== 2) return false;
  const cats = ["architecture", "interiors", "landscape", "planning", "products"];
  return !cats.includes(parts[1]);
}

function newsTabIndex(path: string): number {
  if (path === "/news") return 0;
  const i = NEWS_TABS.indexOf(path as (typeof NEWS_TABS)[number]);
  return i;
}

function stackIndex(path: string): number {
  if (path.startsWith("/news")) return 0;
  const i = STACK.findIndex((s) => path === s || path.startsWith(`${s}/`));
  return i;
}

/** Positive sign = overlay travels right (x) or down (y). */
function travelFor(from: string, to: string): Travel {
  const fromProject = isProjectPath(from) && !isProjectSlug(from);
  const toProject = isProjectPath(to) && !isProjectSlug(to);

  if (fromProject && !toProject) return { axis: "x", sign: 1 };
  if (!fromProject && toProject) return { axis: "x", sign: -1 };

  const fromNews = newsTabIndex(from);
  const toNews = newsTabIndex(to);
  if (fromNews >= 0 && toNews >= 0) {
    return { axis: "y", sign: toNews > fromNews ? -1 : 1 };
  }

  const a = stackIndex(from);
  const b = stackIndex(to);
  if (a >= 0 && b >= 0 && a !== b) {
    return { axis: "y", sign: b > a ? -1 : 1 };
  }

  return { axis: "y", sign: -1 };
}
